// What the ADMIN-ONLY photo dialog (MapPhotoDetailDialog) shows, built from the
// adminPublicPhotos answer. Pure: the translator and the date formatter are
// passed in, so it is unit-tested without Vue. Keys are spelled out in full —
// the client i18n bundle only ships keys a t() call names literally.

import { GetAdminPublicPhotos } from '../graphql/admin-public-photos-graph';
import { formatCoords } from '../helpers/poiLinks';
import type { HighlightPhoto } from '../helpers/highlightDetails';

export interface AdminPublicPhoto {
  id: string;
  mediumUrl: string | null;
  hdUrl: string | null;
  capturedAt: string | null;
  createdAt: string | null;
  platform: string | null;
  appVersion: string | null;
  latitude: number;
  longitude: number;
  elevation: number | null;
  adminUrl: string;
  parent: { type: string; id: string; name: string | null; publicUrl: string | null; adminUrl: string | null } | null;
  uploader: { id: string; name: string | null; adminUrl: string } | null;
}

export type Translate = (key: string, values?: Record<string, unknown>) => string;
export type FormatDate = (iso: string) => string;

export interface PhotoMetaRow { key: string; label: string; value: string }
export interface PhotoLink { label: string; text: string; href: string | null; adminHref: string | null }

export interface PublicPhotoView {
  id: string;
  /** For the lightbox and the carousel: the :medium as the thumb, the :hd full size. */
  photo: HighlightPhoto | null;
  adminUrl: string;
  coordsText: string;
  coordsHref: string;
  elevationMetres: number | null;
  rows: PhotoMetaRow[];
  parent: PhotoLink | null;
  uploader: PhotoLink | null;
}

/** Shown in the date and app rows when there's nothing to show. */
function orUnknown(value: string | null | undefined, t: Translate): string {
  const text = (value || '').trim();
  return text || t('admin_public_photos.dialog.unknown');
}

function parentTypeLabel(type: string, t: Translate): string {
  if (type === 'Recording') return t('admin_public_photos.dialog.recording');
  if (type === 'Ptv2::PlannedTrail') return t('admin_public_photos.dialog.planned_route');
  return type;
}

export function buildPublicPhotoView(photo: AdminPublicPhoto, t: Translate, formatDate: FormatDate): PublicPhotoView {
  const lat = Number(photo.latitude);
  const lon = Number(photo.longitude);
  const app = [photo.platform, photo.appVersion].filter((part) => part && String(part).trim()).join(' ');

  const parent = photo.parent
    ? {
      label: parentTypeLabel(photo.parent.type, t),
      text: photo.parent.name?.trim() || t('admin_public_photos.dialog.untitled', { id: photo.parent.id }),
      href: photo.parent.publicUrl || null,
      adminHref: photo.parent.adminUrl || null,
    }
    : null;

  const uploader = photo.uploader
    ? {
      label: t('admin_public_photos.dialog.uploaded_by'),
      text: photo.uploader.name?.trim() || t('admin_public_photos.dialog.user_number', { id: photo.uploader.id }),
      href: null,
      adminHref: photo.uploader.adminUrl || null,
    }
    : null;

  return {
    id: String(photo.id),
    photo: photo.mediumUrl || photo.hdUrl
      ? { key: String(photo.id), thumbUrl: (photo.mediumUrl || photo.hdUrl) as string, fullUrl: (photo.hdUrl || photo.mediumUrl) as string }
      : null,
    adminUrl: photo.adminUrl,
    coordsText: formatCoords(lat, lon),
    coordsHref: Number.isFinite(lat) && Number.isFinite(lon) ? `/map?lng=${lon.toFixed(5)}&lat=${lat.toFixed(5)}` : '#',
    elevationMetres: typeof photo.elevation === 'number' && Number.isFinite(photo.elevation) ? photo.elevation : null,
    rows: [
      { key: 'captured', label: t('admin_public_photos.dialog.captured'), value: photo.capturedAt ? formatDate(photo.capturedAt) : orUnknown(null, t) },
      { key: 'uploaded', label: t('admin_public_photos.dialog.uploaded'), value: photo.createdAt ? formatDate(photo.createdAt) : orUnknown(null, t) },
      { key: 'app', label: t('admin_public_photos.dialog.app'), value: orUnknown(app, t) },
      { key: 'id', label: t('admin_public_photos.dialog.photo_id'), value: String(photo.id) },
    ],
    parent,
    uploader,
  };
}

/** "2 / 5" — which of the photos stacked under the click is on screen. */
export function stepIndex(current: number, delta: number, count: number): number {
  if (count <= 0) return 0;
  return (((current + delta) % count) + count) % count;
}

export type PublicPhotosLoadResult =
  | { kind: 'shown'; photos: AdminPublicPhoto[] }
  | { kind: 'gone' } // none of them is still public (a stale tile)
  | { kind: 'denied' } // not an admin with two-factor done this session
  | { kind: 'failed'; error: unknown }
  | { kind: 'stale' }; // superseded by a newer click

export interface GraphRequester {
  request(document: any, variables?: Record<string, unknown>): Promise<any>;
}

export type ErrorReporter = (error: unknown, context: Record<string, unknown>) => void;

/** Mutations::AdminOnly::DENIED_MESSAGE, which the query answers a non-admin with. */
export const ADMIN_DENIED_MESSAGE = 'You do not have permission to perform this action';

export function isAdminDeniedError(error: any): boolean {
  const errors = error?.response?.errors;
  return Array.isArray(errors) && errors.some((e: any) => e?.message === ADMIN_DENIED_MESSAGE);
}

/** Fetches a click's photos; a newer click makes an older answer `stale`. */
export class PublicPhotosLoader {
  private latest = 0;

  constructor(
    private readonly client: GraphRequester,
    private readonly reportError: ErrorReporter,
  ) {}

  cancel(): void {
    this.latest += 1;
  }

  async load(ids: string[]): Promise<PublicPhotosLoadResult> {
    const ticket = ++this.latest;
    try {
      const data = await this.client.request(GetAdminPublicPhotos, { ids });
      if (ticket !== this.latest) return { kind: 'stale' };
      const photos: AdminPublicPhoto[] = Array.isArray(data?.adminPublicPhotos) ? data.adminPublicPhotos : [];
      return photos.length ? { kind: 'shown', photos } : { kind: 'gone' };
    } catch (error) {
      if (ticket !== this.latest) return { kind: 'stale' };
      if (isAdminDeniedError(error)) return { kind: 'denied' };
      this.reportError(error, { component: 'admin_public_photos', operation: 'fetch_photos', count: ids.length });
      return { kind: 'failed', error };
    }
  }
}
